/**
 * Utility functions for loading legal documents
 */

export interface LegalDocument {
  filename: string;
  title: string;
  version: string;
}

export const LEGAL_DOCUMENTS = {
  TERMS: {
    filename: 'TERMS_AND_CONDITIONS.md',
    title: 'Terms and Conditions',
    version: '1.0'
  },
  PRIVACY: {
    filename: 'PRIVACY_POLICY.md',
    title: 'Privacy Policy',
    version: '1.0'
  },
  BILLING: {
    filename: 'BILLING_AGREEMENT.md',
    title: 'Billing Agreement',
    version: '1.0'
  }
} as const;

/**
 * Load markdown content from the public/legal directory
 * @param filename - The filename of the markdown document to load
 * @returns Promise that resolves to the markdown content as a string
 */
export const loadLegalDocument = async (filename: string): Promise<string> => {
  try {
    const response = await fetch(`/legal/${filename}`);
    if (!response.ok) {
      throw new Error(`Failed to load ${filename}: ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error(`Error loading legal document ${filename}:`, error);
    throw error;
  }
};

/**
 * Load terms and conditions document
 * @returns Promise that resolves to the terms and conditions markdown content
 */
export const loadTermsAndConditions = (): Promise<string> => {
  return loadLegalDocument(LEGAL_DOCUMENTS.TERMS.filename);
};

/**
 * Load privacy policy document
 * @returns Promise that resolves to the privacy policy markdown content
 */
export const loadPrivacyPolicy = (): Promise<string> => {
  return loadLegalDocument(LEGAL_DOCUMENTS.PRIVACY.filename);
};

/**
 * Load billing agreement document
 * @returns Promise that resolves to the billing agreement markdown content
 */
export const loadBillingAgreement = (): Promise<string> => {
  return loadLegalDocument(LEGAL_DOCUMENTS.BILLING.filename);
}; 